import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from './routes';
import { useAuth } from '../../hooks/useAuth';
import { subscription } from '../../services/api';
import { 
  GamepadIcon, 
  UserCircle, 
  LogOut, 
  Package, 
  Mail, 
  Info
} from 'lucide-react';

const NavBar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const response = await subscription.getCurrentPlan();
        setCurrentPlan(response.data);
      } catch (error) {
        console.error('Error fetching current plan:', error);
      }
    };

    if (user) {
      fetchCurrentPlan();
    }
  }, [user]);

  // Add effect to track scroll position and active sections
  useEffect(() => {
    const handleScroll = () => {
      const contactSection = document.getElementById('contact');
      const supportSection = document.getElementById('support');
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      if (contactSection && supportSection) {
        const contactTop = contactSection.offsetTop;
        const contactBottom = contactSection.offsetTop + contactSection.offsetHeight;
        const supportTop = supportSection.offsetTop;
        const supportBottom = supportSection.offsetTop + supportSection.offsetHeight;

        if (scrollPosition >= contactTop && scrollPosition < contactBottom) {
          setActiveSection('contact');
        } else if (scrollPosition >= supportTop && scrollPosition < supportBottom) {
          setActiveSection('support');
        } else if (location.pathname === ROUTES.About && scrollPosition < supportTop) {
          setActiveSection('about');
        } else if (location.pathname === ROUTES.Services) {
          setActiveSection('services');
        } else {
          setActiveSection('');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const isStrayGamesPage = location.pathname === '/straygames';

  const isActive = (path) => location.pathname === path;
  
  // Function to check if a section is active
  const isSectionActive = (section) => activeSection === section;

  // Handle About link click to scroll to top
  const handleAboutClick = (e) => {
    if (location.pathname === ROUTES.About) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('about');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-900/80 to-blue-900/80 backdrop-blur-md border-b border-white/10">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          
          {/* Logo */}
          <Link 
            to={ROUTES.About} 
            className="flex items-center gap-2 group"
            onClick={handleAboutClick}
          >
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Stray
            </h1>
          </Link>

          {/* Navigation */}
          <nav className="flex-1 ml-12">
            <ul className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-6">
                
                {/* If NOT on StrayGames Page, show Services, About, Contact, Support */}
                {!isStrayGamesPage && (
                  <>
                    <li>
                      <Link
                        to={ROUTES.About}
                        onClick={handleAboutClick}
                        className={`flex items-center gap-2 transition-colors ${
                          (isActive(ROUTES.About) && !isSectionActive('contact') && !isSectionActive('support')) || isSectionActive('about')
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        <Info className="w-4 h-4" />
                        <span>About</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={ROUTES.Services}
                        className={`flex items-center gap-2 transition-colors ${
                          isActive(ROUTES.Services)
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        <Package className="w-4 h-4" />
                        <span>Services</span>
                      </Link>
                    </li>
                    <li>
                      <a
                        href="#contact"
                        className={`flex items-center gap-2 transition-colors ${
                          isSectionActive('contact')
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        <Mail className="w-4 h-4" />
                        <span>Contact</span>
                      </a>
                    </li>
                    <li>
                      <a
                        href="#support"
                        className={`flex items-center gap-2 transition-colors ${
                          isSectionActive('support')
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        <Mail className="w-4 h-4" />
                        <span>Support</span>
                      </a>
                    </li>
                  </>
                )}
                
                {/* If on StrayGames Page and NOT logged in, show Login & Register */}
                {isStrayGamesPage && !user && (
                  <>
                    <li>
                      <Link
                        to={ROUTES.LOGIN}
                        className={`px-6 py-2 transition-colors ${
                          isActive(ROUTES.LOGIN)
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        Login
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={ROUTES.REGISTER}
                        className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium 
                        hover:from-purple-600 hover:to-pink-600 transition-colors"
                      >
                        Register
                      </Link>
                    </li>
                  </>
                )}

                {/* If user is logged in (on any page), show My Games & Profile */}
                {user && !isStrayGamesPage && (
                  <>
                    <li>
                      <Link
                        to={ROUTES.GAME_LIBRARY}
                        className={`flex items-center gap-2 transition-colors ${
                          isActive(ROUTES.GAME_LIBRARY)
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        <GamepadIcon className="w-4 h-4" />
                        <span>My Games</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={ROUTES.PROFILE}
                        className={`flex items-center gap-2 transition-colors ${
                          isActive(ROUTES.PROFILE)
                            ? 'text-white font-bold'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        <UserCircle className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>
                    </li>
                  </>
                )}
              </div>

              {/* Logout & Subscription Plan (if user is logged in) */}
              {user && (
                <div className="flex items-center gap-4">
                  {currentPlan && (
                    <Link to={ROUTES.SUBSCRIPTION} className="px-4 py-1.5 rounded-full bg-gradient-to-r from-green-500/10 to-blue-500/10 
                      text-green-400 text-sm font-medium border border-green-500/20 hover:border-green-500/40 
                      transition-all duration-300">
                      {currentPlan.name}
                    </Link>
                  )}
                  <button onClick={logout} className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-red-500/10 to-orange-500/10 
                    text-red-400 text-sm font-medium border border-red-500/20 hover:border-red-500/40 
                    transition-all duration-300">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default NavBar;